import { CSSProperties } from "react";
import I18n from "common/i18n";
import { PlanLimits, QuotaStatus, usePlanLimits } from "features/subscription";
import { PLAN_COMPARISON_ITEMS, formatPlanLimit } from "features/user/constants";

const UsageRow = ({
  icon,
  label,
  color,
  format,
  unitKey,
  quota,
}: {
  icon: string;
  label: string;
  color: string;
  format?: "bytes";
  unitKey?: string;
  quota: QuotaStatus;
}) => {
  const percent = quota.unlimited
    ? 100
    : quota.limit > 0
      ? Math.min((quota.used / quota.limit) * 100, 100)
      : 0;
  const unitSuffix = format === "bytes" || !unitKey ? "" : I18n.t(unitKey);
  const usedText =
    format === "bytes" ? formatPlanLimit(quota.used, false, format) : String(quota.used);
  const limitText = quota.unlimited
    ? formatPlanLimit(quota.limit, quota.unlimited, format)
    : formatPlanLimit(quota.limit, false, format);
  const isUnlimited = quota.unlimited;
  const isAccessDenied = quota.accessDenied;

  return (
    <div
      className="flex-row-el flex-1 min-h-0 items-center gap-3 w-full"
      style={{ "--row-color": color, "--bar-width": `${percent}%` } as CSSProperties}
    >
      <span className="material-symbols-outlined type-hero shrink-0 text-[color:var(--row-color)]">
        {icon}
      </span>
      <div className="flex-col-el flex-1 min-w-0 gap-1">
        <div className="flex-row-el items-baseline justify-between gap-2 w-full">
          <p className="type-text type-body text-textcolor-black leading-tight truncate">{label}</p>
          {isAccessDenied ? (
            <p className="type-text type-body leading-tight whitespace-nowrap shrink-0 text-[color:var(--row-color)]">
              —
            </p>
          ) : (
            <div className="flex-row-el items-baseline gap-1 shrink-0">
              <p className="type-text type-body leading-tight whitespace-nowrap text-[color:var(--row-color)]">
                {`${usedText} / ${limitText}`}
              </p>
              {!isUnlimited && unitSuffix && (
                <p className="type-text type-sm leading-tight whitespace-nowrap text-[color:var(--row-color)]">
                  {unitSuffix}
                </p>
              )}
            </div>
          )}
        </div>
        <div className="flex-col-el w-full h-[14px] rounded-full bg-gray2-40 overflow-hidden">
          <div className="h-full rounded-full transition-all bg-[color:var(--row-color)] w-[var(--bar-width)]" />
        </div>
      </div>
    </div>
  );
};

export const UserPlanCurrentUsageCard = () => {
  const limits: PlanLimits = usePlanLimits();

  return (
    <div className="flex-col-el flex-1 min-h-0 rounded-2xl bg-white border-2 border-solid border-beige-60 p-4 gap-3 overflow-hidden">
      <div className="flex-col-el items-start shrink-0">
        <p className="type-text type-title text-blue-100 leading-tight">
          {I18n.t("MSG_USER_PLAN_USAGE_CARD_TITLE")}
        </p>
        <p className="type-text type-sm text-textcolor-black/60 leading-tight">
          {I18n.t("MSG_USER_PLAN_USAGE_CARD_SUBTITLE")}
        </p>
      </div>
      <div className="flex-col-el flex-1 min-h-0 justify-around gap-2">
        {PLAN_COMPARISON_ITEMS.filter((item) => item.key !== "aiImagePerRequest").map((item) => (
          <UsageRow
            key={item.key}
            icon={item.icon}
            label={I18n.t(item.labelKey)}
            color={item.color}
            format={item.format}
            unitKey={item.unitKey}
            quota={limits[item.key]}
          />
        ))}
      </div>
    </div>
  );
};
